import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar1 } from '../components/shadcnblocks-com-navbar1';
import { Footer } from '../components/ui/footer';
import { Button } from '../components/ui/button';
import FondoDeGranos, { FONDO_ANCHO, FONDO_ANGOSTO, RESPALDO_LANDING, usePantallaAncha } from '../components/business/FondoDeGranos';
import VariableProximity from '../components/reactbits/variable-proximity';
import FilaDeFuncionalidad from '../components/business/FilaDeFuncionalidad';
import CapturaDeProducto from '../components/business/CapturaDeProducto';
import MosaicoFuncionalidades from '../components/business/MosaicoFuncionalidades';
import Faq2 from '../components/mvpblocks/faq-2';
import TarjetaDePlan from '../components/business/TarjetaDePlan';
import { planesDeContratacion } from '../data/planes';
import TelefonoDelHero from '../components/business/TelefonoDelHero';
import SEO from '../components/SEO';
import useScrollSuave from '../hooks/useScrollSuave';
import { footerData } from '../data/navegacion';
import { faqs } from '../data/faqs';
// Los mismos rubros que el onboarding y el panel, para que la landing no
// prometa un rubro que después no se puede elegir.
import { rubros } from '../data/rubros';
import {
  organizationSchema,
  softwareApplicationSchema,
  webSiteSchema,
  serviceSchema,
  faqSchema,
} from '../utils/schemas';

/**
 * La landing. Es lo que se ve en "/"; /landing-2 —donde vivió mientras se
 * escribía— redirige acá. Home.jsx pasó a /landing-1 y quedó sólo para
 * comparar.
 *
 *
 * QUÉ SE TIRÓ Y POR QUÉ
 *
 * La versión anterior de este archivo tenía 820 líneas construidas con el kit
 * que usan todas las landings generadas: fondo aurora animado, titular con
 * cuatro adjetivos rotando con emoji ("Automático 🤖", "Profesional 💼"),
 * mosaico de once tarjetas iguales con un ícono de lucide cada una, bandas
 * grises alternadas y todo centrado.
 *
 * No se sacó por gusto. Un relevamiento de 3,2 millones de posts de Reddit
 * sobre sitios "vibe-codeados" rankea las señales que la gente reconoce a
 * simple vista, y las primeras son exactamente esas: defaults de
 * shadcn/Tailwind, gradientes, glassmorphism, íconos de lucide por todos lados,
 * texto con gradiente en el titular y una etiqueta "eyebrow" arriba de cada
 * sección.
 *
 * REGLAS PARA LO QUE SE AGREGUE ACÁ ABAJO:
 *
 *   - Nada de gradientes de fondo, `backdrop-blur` ni resplandores.
 *   - Nada de pastillas/eyebrow arriba de las secciones.
 *   - Los íconos no son el sistema visual. Si un bloque se explica con
 *     tipografía, va con tipografía. En el hero no hay ni uno.
 *   - Nada de rayas de color en el borde izquierdo de una tarjeta.
 *   - Sin emojis en los titulares.
 *   - Nada de prueba social: todavía no hay clientes, y un contador inventado
 *     de negocios es lo que hace que el resto de la página deje de creerse.
 *
 *
 * DE DÓNDE SALE LA PERSONALIDAD, que es lo que reemplaza al gradiente:
 *
 *   1. La TIPOGRAFÍA. Bricolage Grotesque en vez de Inter, que es la fuente por
 *      defecto de shadcn y la señal más repetida del ranking. Es variable, y el
 *      titular usa esos ejes: las letras engordan cuando el cursor pasa cerca.
 *      El texto está completo y quieto desde el primer frame, así que no
 *      retrasa la lectura; lo que se mueve es el trazo.
 *   2. El GRANO. Un filtro SVG sobre el fondo. Un blanco liso no es sobrio, es
 *      plano: no tiene superficie.
 *   3. El TABLERO. Un split-flap de aeropuerto con las horas a las que entran
 *      turnos. El producto son horarios y ese es el objeto que muestra
 *      horarios: es lo único de esta página que no podría estar en la landing
 *      de otro producto.
 *
 *
 * ESTADO: el hero, terminado. Las secciones 2 a 9 entran después, una por vez.
 */

/* Los ejes de Bricolage para el titular. La variación de ancho es chica a
   propósito: con `wdth` moviéndose mucho las palabras cambian de largo y la
   línea baila, que es justo lo que un titular no puede hacer. */
const TRAZO_EN_REPOSO = "'wght' 500, 'wdth' 100";
const TRAZO_BAJO_EL_CURSOR = "'wght' 800, 'wdth' 92";

/**
 * El orden de los rubros en esta landing, que NO es el de data/rubros.js.
 *
 * Ahí el orden lo manda el onboarding y no hay razón para tocarlo — cambiarlo
 * reordenaría también el primer paso del alta y las tarjetas de Home. Acá el
 * orden es una decisión comercial: los primeros son los rubros que Aturno está
 * saliendo a buscar hoy, consultorios y profesionales del bienestar, que además
 * son los únicos con un módulo propio en el producto (el seguimiento de
 * pacientes). Peluquerías y canchas están, y bien, pero no van adelante.
 *
 * Lo que no está en la lista se agrega igual, al final: si mañana entra un rubro
 * nuevo en data/rubros.js tiene que aparecer solo. Filtrando por esta lista, un
 * rubro nuevo quedaría invisible en la landing y nadie se enteraría.
 */
const RUBROS_PRIMERO = ['health', 'services', 'beauty', 'fitness', 'sports', 'education'];
const RUBROS_DE_LA_LANDING = [
  ...RUBROS_PRIMERO.map((id) => rubros.find((r) => r.id === id)).filter(Boolean),
  ...rubros.filter((r) => !RUBROS_PRIMERO.includes(r.id)),
];

const LandingDos = () => {
  const navigate = useNavigate();
  const { currentUser, userData, loading } = useAuth();
  const cajaDelTitular = useRef(null);
  const pantallaAncha = usePantallaAncha();

  const scrollSuave = useScrollSuave();

  // Un usuario logueado no ve la landing: va al panel, o a terminar el
  // onboarding si lo dejó por la mitad.
  useEffect(() => {
    if (loading || !currentUser || !userData) return;
    navigate(userData.onboardingCompleted ? '/admin/dashboard' : '/admin/onboarding', {
      replace: true,
    });
  }, [currentUser, userData, loading, navigate]);


  // Si la inercia está activa, el salto lo tiene que hacer ella: usar
  // `window.scrollTo` mientras lenis corre es pelearle por la misma página.
  const irA = (id) => {
    const elemento = document.getElementById(id);
    if (!elemento) return;
    const alturaDeLaBarra = 80;
    const lenis = scrollSuave.current;
    if (lenis) {
      lenis.scrollTo(elemento, { offset: -alturaDeLaBarra });
      return;
    }
    window.scrollTo({ top: elemento.offsetTop - alturaDeLaBarra, behavior: 'smooth' });
  };

  /* El menú sólo enlaza lo que existe. La versión anterior ofrecía
     "Características", "Precios" y "Preguntas" cuando esas secciones no están
     escritas, y un menú que scrollea a la nada es peor que un menú corto. */
  const configDeLaBarra = {
    logo: {
      url: '#inicio',
      src: '/logo-aturno.png',
      alt: 'Aturno',
      title: 'Aturno',
      onClick: () => irA('inicio'),
    },
    /* UNA ENTRADA POR SECCIÓN QUE EXISTE, en el orden en que aparecen. Estuvo
       con "Inicio" solo mientras las demás no estaban escritas —un menú que
       scrollea a la nada es peor que un menú corto— y ahora están las cinco.

       "Preguntas" apunta a #recursos, que suena raro y NO es un descuido: el
       footer compartido (data/navegacion.js) enlaza /#recursos y Home usa ese
       mismo id para su bloque de FAQ. Renombrarlo acá dejaría el enlace del
       footer sin destino en esta página. */
    menu: [
      { title: 'Inicio', url: '#inicio', onClick: () => irA('inicio') },
      // Dice "Cómo funciona" pero apunta a #producto: la etiqueta es lo que lee
      // el visitante —"Producto" es la palabra del que vende, no la del que
      // compra— y el id se queda como está porque lo usa la sección.
      { title: 'Cómo funciona', url: '#producto', onClick: () => irA('producto') },
      { title: 'Para quién es', url: '#rubros', onClick: () => irA('rubros') },
      { title: 'Planes', url: '#precios', onClick: () => irA('precios') },
      { title: 'Preguntas', url: '#recursos', onClick: () => irA('recursos') },
    ],
    mobileExtraLinks: [
      { name: 'Ver demo', url: '/demo' },
      { name: 'Crear cuenta', url: '/auth/register' },
    ],
    auth: {
      login: { text: 'Iniciar sesión', url: '/auth/login' },
      signup: { text: 'Registrarse', url: '/auth/register' },
    },
  };

  // Mientras el efecto de arriba redirige, pintar la landing sería un
  // parpadeo. Se exige `userData`: si no llegó, es mejor mostrar la landing que
  // dejar la pantalla en blanco.
  if (!loading && currentUser && userData) return null;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        /* El título y la descripción nombran al público que estamos saliendo a
           buscar —consultorios y centros con varios profesionales— y no "un
           negocio" en general. Quien busca esto escribe "turnos para
           consultorio", no "sistema de gestión". */
        title="Turnos online para consultorios y centros con equipo | Aturno"
        description="Tus pacientes reservan solos, por tu página y por WhatsApp. Cada turno cae en la agenda del profesional y la sala que corresponde. Probalo 14 días gratis, sin tarjeta."
        keywords="turnos online consultorio, agenda para consultorio médico, software turnos odontología, turnos para coaching, bot de whatsapp para turnos, sistema de turnos argentina"
        url="https://aturno.app"
        image="/logo-aturno.png"
        type="website"
        schema={[
          organizationSchema,
          softwareApplicationSchema,
          webSiteSchema,
          serviceSchema,
          faqSchema(faqs),
        ]}
      />

      {/* LA PRIMERA PANTALLA, con el fondo detrás de todo lo que hay en ella.

          Esta caja envuelve la barra Y el hero, y es lo que define hasta dónde
          llega el color. Antes hubo dos versiones equivocadas, en direcciones
          opuestas:

            - Con el fondo adentro del `<section>`, la barra —que es
              `sticky top-0` y va antes en el flujo— quedaba sobre blanco y se
              veía una franja cortando el color arriba.
            - Con el fondo en el contenedor de página, se comía la landing
              entera, incluidas las secciones que vengan después.

          Envolviendo barra + hero queda lo correcto: el color cubre la pantalla
          de inicio de punta a punta y termina donde termina el hero.

          `overflow-hidden` acá hace dos trabajos: recorta el fondo contra el
          borde y recorta las maquetas, que se salen a propósito.

          La posición es la misma que la de las pantallas de auth: quien llega a
          "Ingresar" viene de acá, y con dos configuraciones distintas el fondo
          cambiaría sin motivo al pasar de una a la otra. */}
      {/* `overflow-x-clip` Y NO `overflow-hidden`, y la diferencia es la barra.

          `overflow: hidden` convierte al elemento en CONTENEDOR DE SCROLL, y un
          `position: sticky` se pega contra su contenedor de scroll más cercano
          — no contra la ventana. Como este div no scrollea (scrollea el
          documento), la barra quedaba pegada a algo que nunca se mueve, o sea
          que se iba con la página. Medido: a scrollY=6000 la barra estaba en
          top=-6000. En Home no pasa porque su barra no tiene ningún ancestro
          con overflow.

          `clip` recorta igual pero NO crea contenedor de scroll, así que la
          barra vuelve a pegarse a la ventana. Y alcanza con el eje horizontal:
          lo único que hay que contener es el desborde lateral. La sombra de
          9999px del marco no necesita recorte vertical porque las sombras no
          suman área scrolleable. */}
      <div className="relative overflow-x-clip">
        {/* `<main>` arranca acá y no después de la barra: la caja de la
            primera pantalla contiene la barra (el color le pasa por detrás) y
            el hero, así que no hay dónde abrirlo en el medio sin partirla. */}
        <main>
        {/* LA PRIMERA PANTALLA: barra + hero, en su propia caja.

            Existe para que el fondo Y el marco se midan contra algo REAL en
            vez de contra `100svh`.

            `GrainGradient` se posiciona `absolute inset-0`, así que llena al
            ancestro posicionado más cercano. Sin esta caja llenaba el
            envoltorio entero —3460px con la sección de producto adentro— y el
            shader reparte el color según la FORMA del lienzo, así que las
            manchas se iban a cualquier lado. El primer arreglo fue darle una
            caja de `h-[100svh]`, y funcionaba… mientras la cuenta cerrara: la
            barra ocupa 7rem y el hero es `min-h-[calc(100svh-7rem)]`, o sea
            que barra + hero suman una pantalla EXACTA. Pero es una cuenta a
            mano, y el marco blanco la heredó: en cuanto el alto real no
            coincide con `100svh`, su borde de abajo se va fuera de cuadro y
            el color queda cortado a filo de pantalla, sin curva.

            Con una caja de verdad, los dos son `inset-0` e `inset-3` y no hay
            ninguna cuenta que pueda dejar de cerrar. */}
        {/* `overflow-hidden` ACÁ ES OBLIGATORIO, no cosmético: es lo que recorta
            la sombra de 9999px del marco blanco de más abajo.

            Esa sombra pinta blanco hacia AFUERA en todas las direcciones, y sin
            algo que la recorte se derrama por toda la página. Estuvo derramada:
            el marco confiaba en el `overflow-hidden` del envoltorio de página
            —lo dice su propio comentario— y ese envoltorio pasó a
            `overflow-x-clip` para arreglar la barra pegajosa. `clip` en X no
            recorta en Y, así que la sombra bajó libre y tapó de blanco todo lo
            que estuviera por debajo de `z-[5]`: el footer entero quedó en
            blanco, y las secciones se salvaron sólo porque llevan `z-10`.

            Costó encontrarlo porque no se comporta como algo que tape: es
            `pointer-events: none`, así que el hit-testing no lo ve, y pinta con
            `box-shadow` y no con `background`, así que buscar "quién tiene
            fondo acá" tampoco lo encuentra.

            Recortando en esta caja, la sombra sólo blanquea los márgenes del
            hero, que es para lo que está. */}
        <div className="relative overflow-hidden">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            <FondoDeGranos
              posicion={pantallaAncha ? FONDO_ANCHO : FONDO_ANGOSTO}
              respaldo={RESPALDO_LANDING}
            />
          </div>

          {/* EL MARCO BLANCO.

              Recorta el bloque de color en un rectángulo de esquinas
              redondeadas, y por fuera de la curva asoma el blanco de la
              página. La curva pertenece al blanco, no al color: es un marco
              apoyado ENCIMA, no el fondo recortado.

              CÓMO ESTÁ HECHO, que es lo no obvio: una caja vacía metida hacia
              adentro por el grosor del marco, con `border-radius` y una
              sombra de 9999px hacia AFUERA. Una sombra exterior sigue el
              redondeo del elemento, así que pinta de blanco todo lo que queda
              por fuera de la curva —los cuatro lados y, sobre todo, las cuatro
              esquinas— con un solo elemento y sin tocar la maquetación. El
              `overflow-hidden` del envoltorio recorta esos 9999px, así que no
              generan scroll.

              `z-[5]`: por encima del fondo, por debajo del contenido del hero
              (`z-10`) y de la barra (`z-50`). */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-3 z-[5] rounded-[2rem] shadow-[0_0_0_9999px_#fff]"
          />

          {/* `fixed` Y NO `sticky`, y no es capricho.

              La barra vive DENTRO de la caja de la primera pantalla, porque el
              color tiene que pasarle por detrás. Pero `sticky` se pega contra
              su ancestro de bloque, y esa caja mide una pantalla: medido, la
              barra acompañaba hasta scrollY=788 y a partir de ahí se iba. Para
              que `sticky` sirviera habría que sacarla de la caja, y entonces
              el color deja de estar detrás.

              `fixed` se posiciona contra la ventana sin importar en qué caja
              esté, así que resuelve las dos cosas a la vez. Y de paso saca de
              la cuenta el alto de la barra: al no ocupar lugar en el flujo, la
              caja mide exactamente el hero y el hero puede pedir `100svh`
              limpio en vez de `calc(100svh - 7rem)`. */}
          <Navbar1 {...configDeLaBarra} clasePosicion="fixed inset-x-0 top-0" />

        <section
          id="inicio"
          className="relative z-10 flex min-h-[100svh] items-center overflow-hidden px-5 pb-24 pt-28 sm:px-8 sm:pb-28 sm:pt-32 lg:px-10 2xl:px-14"
        >
          {/* SIN `max-w` ACÁ, y es lo que faltaba para el titular de una línea.
              El contenedor estaba en `max-w-6xl` (1152px) y el titular necesita
              1801px: sacarle su propio `max-w-3xl` no alcanzaba, porque quien lo
              cortaba era el padre. Ahora cada hijo declara el suyo. */}
          <div className="relative z-10 w-full">
            {/* UNA SOLA COLUMNA, CENTRADA. Antes esto era una grilla de dos con las
                maquetas a la derecha; el teléfono con la secuencia de WhatsApp y
                agenda se sacó del hero y quedó en
                components/business/TelefonoDelHero.jsx, entero y listo para
                volver a montarse donde corresponda.

                Centrado el texto, la lectura ya no sigue el patrón F: acá el ojo
                cae al medio. Por eso el ancho está limitado —`max-w-3xl` en el
                titular, menos en la bajada—: una línea centrada y larga es la
                peor combinación para leer, porque en cada salto de renglón hay
                que buscar dónde empieza la siguiente. */}
              <div className="flex flex-col items-center text-center">
              {/* ---------------- El argumento ---------------- */}
              {/* SIN PANEL NI VELO DETRÁS DEL TEXTO, y es una decisión tomada
                  después de probar los dos y que fallaran igual.

                  El texto se leía mal sobre el fondo, y las dos veces la
                  respuesta fue agregar una capa: primero un velo blanco, después
                  una placa de vidrio. Las dos resuelven la lectura tapando
                  justo lo que hace linda a la página, y las dos suman un objeto
                  que la referencia no tiene: ahí el titular se apoya directo
                  sobre el fondo.

                  Lo que la referencia sí hace, y nosotros no hacíamos, es tener
                  el fondo LIMPIO detrás del texto: todo el color vive del lado
                  de las maquetas. O sea que nunca fue un problema de panel, era
                  de dónde se para la mancha. Se resuelve en la posición del
                  shader, y esa posición está medida — ver FondoDeGranos. */}
              <div className="flex w-full flex-col items-center">
                {/* Acá NO va una pastilla que diga "El software que tu negocio
                    necesita". La etiqueta arriba del titular es una de las
                    señales más reconocibles de página generada. */}
                {/* 8.4vw NO ES A OJO: es el punto medido donde
                    "Tu negocio toma turnos solo." llena el ancho útil sin
                    desbordar, y da el mismo valor en 1920, 1512 y 1280. Antes el
                    titular se cortaba en dos líneas, y la causa no era el tamaño
                    sino un `max-w-3xl` que lo estrangulaba: sacándolo entra en
                    una línea Y 60% más grande.

                    El piso de 2.6rem del `clamp` es para teléfono, donde 8.4vw
                    serían 33px. Ahí tiene que cortar en varias líneas, y está
                    bien que lo haga. */}
                <h1
                  ref={cajaDelTitular}
                  className="w-full text-balance font-titular text-[clamp(2.4rem,6vw,7.5rem)] leading-[0.94] tracking-[-0.045em] text-neutral-950"
                >
                  {/* Cinco palabras, y esa cuenta es la restricción real: el
                      titular se dibuja a 6vw, así que cada palabra de más lo
                      parte en otra línea y el hero deja de leerse de un saque.

                      Se probó "Tomás turnos aunque estés atendiendo" —nombra
                      mejor la escena, pero son 37 caracteres: dos líneas en
                      escritorio y tres en teléfono— y se volvió a ésta. Si
                      alguna vez se cambia por una frase larga, hay que partirla
                      a mano, no dejar que el balanceo decida dónde corta. */}
                  <VariableProximity
                    label="Tu negocio toma turnos solo."
                    containerRef={cajaDelTitular}
                    fromFontVariationSettings={TRAZO_EN_REPOSO}
                    toFontVariationSettings={TRAZO_BAJO_EL_CURSOR}
                    radius={160}
                    falloff="gaussian"
                  />
                </h1>

                {/* Una frase por línea, con el corte EXPLÍCITO. Antes cortaba
                    ahí por casualidad —dependía del ancho disponible— así que
                    al cambiar el tamaño de la ventana se reacomodaba solo y a
                    veces partía una frase por la mitad. En teléfono el `hidden`
                    lo desactiva y el texto fluye natural.

                    Las dos palabras marcadas son las dos puertas por donde entra
                    un turno, y cada una lleva el color de lo que nombra: el
                    naranja de Aturno para tu página, el verde de WhatsApp para
                    WhatsApp. No es decoración: es el mismo verde que el usuario
                    ve todos los días en su teléfono, así que la palabra se
                    reconoce antes de leerse. */}
                <p className="mx-auto mt-7 max-w-3xl text-balance font-titular text-[1.4rem] font-medium leading-snug text-neutral-800 sm:text-[1.6rem] 2xl:text-[1.85rem]">
                  Por tu{' '}
                  <span className="rounded-md bg-[#FF5722] px-2 py-0.5 text-white">página</span>
                  {' '}y por tu{' '}
                  <span className="rounded-md bg-[#25D366] px-2 py-0.5 text-white">WhatsApp</span>.
                  <br className="hidden sm:block" />
                  {' '}A las tres de la mañana también.
                </p>

                {/* Los dos botones miden lo mismo. Antes el secundario era texto
                    subrayado al lado de una pastilla grande: no se leía como un
                    botón secundario sino como un link olvidado. Siguen sin
                    competir, porque son intenciones distintas —evaluar contra
                    comprometerse—, pero ahora los dos son objetos. */}
                <div className="mt-10 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                  <Button size="cta" onClick={() => navigate('/auth/register')}>
                    Probar 14 días gratis
                  </Button>
                  <Button
                    size="cta"
                    variant="ghost"
                    onClick={() => navigate('/demo')}
                    className="border border-neutral-900/25 text-neutral-900 hover:border-neutral-900/50 hover:bg-neutral-900/5 hover:text-neutral-900"
                  >
                    Ver la demo
                  </Button>
                </div>

                {/* La promesa de los 15 minutos va acá y no arriba del titular:
                    una etiqueta sobre el titular es el patrón "eyebrow", que es
                    una de las señales de página generada que este archivo evita.
                    Acá abajo es donde el visitante busca los motivos para no
                    dudar, que es exactamente lo que esta frase es.

                    El número es consistente con lo que el producto ya afirma en
                    otros lados: ComoFunciona.jsx dice "te lleva 5 minutos" para
                    la página, y data/faqs.js dice que la configuración inicial
                    lleva unos minutos. No se promete nada nuevo. */}
                <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-neutral-600">
                  <strong className="font-semibold text-neutral-900">
                    Lo dejás andando en menos de 15 minutos.
                  </strong>{' '}
                  {/* "por turno" y no "por reserva": es la palabra que usa un
                      consultorio, y la comisión por operación es la objeción
                      concreta contra la que se compara este producto. */}
                  Sin tarjeta, sin comisión por turno.
                </p>


              </div>
            </div>
          </div>
          </section>
        </div>

        {/* ===== El producto =====

            La sección que el hero perdió. El hero quedó siendo titular,
            subtítulo y botones: una promesa sin ninguna evidencia de que
            Aturno exista. Acá está el producto, y son capturas REALES de
            /demo/business y /demo/dashboard, no ilustraciones.

            EL TITULAR DE CADA FILA ES EL PROBLEMA, no el nombre del feature.
            Una sección de problemas sola es una sección sin producto adentro,
            y encima se la lee alguien que ya conoce su problema porque lo vive
            todos los días. Pero al revés tampoco: "Página de reservas" es una
            categoría. El problema arriba y la captura al lado contestándolo.

            `overflow-hidden` NO ES COSMÉTICO: es lo que recorta las capturas
            contra el borde. `CapturaDeProducto` se desborda a propósito y
            depende de que quien la contiene la corte. */}
        <section
          id="producto"
          /* `bg-white` NO ES DECORACIÓN: tapa el fondo de granos.
             El shader está posicionado para el hero —medido, con las manchas
             en las esquinas y limpio detrás del texto— pero el <div> que lo
             contiene abarca toda la página, así que la mancha de abajo a la
             derecha caía justo encima de esta columna de texto y la dejaba
             ilegible. El fondo artístico es del hero; de acá para abajo el
             producto necesita papel blanco. */
          className="relative z-10 overflow-hidden bg-white px-5 py-24 sm:px-8 sm:py-32 lg:px-10 2xl:px-14"
        >
          {/* El encabezado que la sección no tenía.

              Las tres filas empiezan cada una con un problema, pero sin nada
              arriba que lo anunciara, la primera se leía como una afirmación
              suelta sobre el visitante. Con la pregunta adelante, las tres
              pasan a ser una lista que él va tildando solo, y la captura de al
              lado es la respuesta.

              Va FUERA del contenedor de las filas y con su propio margen: ese
              contenedor separa con `gap-28 sm:gap-36`, que es el aire que
              necesitan dos filas del mismo peso entre sí y demasiado entre un
              encabezado y lo que encabeza. */}
          <header className="mx-auto mb-16 w-full max-w-7xl sm:mb-20">
            <div className="max-w-2xl">
              <h2 className="text-balance font-titular text-[clamp(1.8rem,3vw,2.6rem)] font-bold leading-[1.08] tracking-[-0.03em] text-neutral-950">
                Decime si algo de esto te pasa.
              </h2>
              <p className="mt-4 text-pretty text-base leading-relaxed text-neutral-600 sm:text-lg">
                Son tres cosas de todos los días en cualquier lugar que trabaja
                con turnos. Al lado de cada una, qué hace Aturno con eso.
              </p>
            </div>
          </header>

          <div className="mx-auto flex w-full max-w-7xl flex-col gap-28 sm:gap-36">
            <FilaDeFuncionalidad
              etiqueta="Tu página"
              tono="naranja"
              /* Cada fila se escribe con la misma receta, y conviene respetarla
                 al editar: el TÍTULO es una escena de su día, en presente y sin
                 nombrar al producto; la BAJADA dice en una frase qué pone
                 Aturno en el lugar de esa escena; los PUNTOS son tres hechos
                 concretos, uno por línea, sin adjetivos. Antes los tres niveles
                 decían lo mismo con distintas palabras y no se entendía cuál
                 era el problema y cuál la solución. */
              titulo="El teléfono suena mientras atendés."
              bajada="Aturno te da tu propia página de turnos. Le pasás el link, la persona elige el día y la hora que le sirve, y el turno queda tomado sin que vos cortes lo que estás haciendo."
              puntos={[
                'Ve sólo los horarios que tenés libres de verdad.',
                'Elige con qué profesional y en qué consultorio, no nada más la hora.',
                'Cancela o cambia el turno sola, con un código y sin crear ninguna cuenta.',
              ]}
              nota="Con tu color y tu logo, en aturno.app/tunegocio."
            >
              <CapturaDeProducto
                src="/producto-reservas.png"
                alt="La página pública de una peluquería: su logo y su nombre sobre una franja del color del negocio, el horario de hoy, y los tres servicios con su duración y su precio."
              />
            </FilaDeFuncionalidad>

            {/* Fila 2 — el WhatsApp. Es el único visual que no es una captura
                quieta, y va así a propósito: lo que hay que probar acá es que
                el bot ENTIENDE, y eso no se ve en una foto de un chat. La
                conversación se escribe sola y después la pantalla se desliza a
                la agenda con el turno ya cargado.
                Las dos trampas del guion —"con la misma chica de la otra vez"
                y "el de las 4 y media"— son las que un bot de menú de botones
                no pasa. Están explicadas en TelefonoDelHero.jsx. */}
            <FilaDeFuncionalidad
              etiqueta="Tu WhatsApp"
              tono="verde"
              titulo="Todo el día contestando las mismas preguntas."
              bajada="Aturno contesta por vos en tu WhatsApp: cuánto sale, dónde quedan, qué obras sociales atendés. Y si la persona quiere turno, se lo da ahí mismo."
              puntos={[
                'Entiende cómo escribe la gente: “el jueves con la misma de la otra vez”.',
                'Le cargás las respuestas una vez y las repite siempre igual.',
                'Cuando querés contestar vos, le sacás la conversación desde el panel.',
              ]}
              nota="Contesta de madrugada, los domingos y mientras estás atendiendo."
              visualAngosto
              invertida
            >
              {/* `w-full` y no un ancho fijo con `mx-auto`: el teléfono medía 22rem
                  centrado en una columna de ~517px, así que le sobraban 80px a
                  cada lado y su borde derecho no llegaba a donde termina el
                  texto de la fila de arriba. Llenando la columna, los dos
                  bordes caen sobre la misma línea que el resto de la sección
                  —que es lo único que hace que tres filas distintas se lean
                  como una sola cosa— y de paso el chat se lee sin forzar. */}
              <TelefonoDelHero className="w-full" />
            </FilaDeFuncionalidad>

            {/* Fila 3 — la agenda. Cierra la secuencia: las dos filas de
                arriba son POR DÓNDE ENTRA el turno y ésta es DÓNDE CAE. Con
                eso queda dicho que es un producto terminado y no un formulario
                suelto, sin tener que escribir la frase. */}
            <FilaDeFuncionalidad
              etiqueta="Tu agenda"
              tono="violeta"
              /* Antes decía "El cuaderno, la agenda del celular y los mensajes
                 sin leer": el desorden de una persona sola, y encima una lista
                 de tres objetos que no dice qué sale mal. El público que
                 buscamos tiene equipo, y lo que le sale mal tiene nombre: dos
                 personas anotadas a la misma hora en el mismo lugar. */
              titulo="Dos anotados a la misma hora."
              bajada="Todos los turnos caen en una sola agenda: los de tu página, los de WhatsApp y los que cargás vos. Cada uno con su profesional y su consultorio, así no se pisan."
              puntos={[
                'Un turno nuevo aparece solo, sin refrescar la pantalla.',
                'El que llegó por teléfono o en el mostrador lo cargás a mano.',
                'Tapás un rato, un día o dos semanas y deja de ofrecerse.',
              ]}
            >
              <CapturaDeProducto
                src="/producto-agenda.png"
                alt="El panel de turnos de una peluquería: la semana del 20 al 26 de agosto con siete turnos repartidos entre el jueves y el viernes."
              />
            </FilaDeFuncionalidad>

            {/* TODO LO DEMÁS, en el mosaico de la landing 1.

                Acá hubo una lista tipográfica en tres columnas, escrita para
                evitar el formato "grilla de tarjetitas con ícono", que es de
                las señales más reconocibles de página generada. Se reemplaza
                a pedido por el mosaico que ya existe en Home, y la objeción
                queda anotada por si vuelve a discutirse.

                A favor del mosaico, y no es poco: sus piezas no son títulos
                con un ícono al lado — cada una trae su propio dibujo (la
                grilla de precios, los tres bloqueos de ejemplo, la lista de
                calendarios, los contadores), que es justo lo que una lista de
                texto no puede mostrar. Y al compartir componente con Home, un
                feature nuevo entra una sola vez en los dos lados.

                OJO AL EDITARLO: lo monta también Home.jsx. Y sus anchos tienen
                que cerrar filas de cuatro en lg — está explicado en su propio
                encabezado. */}
            <div>
              <h3 className="max-w-2xl text-balance font-titular text-[clamp(1.6rem,2.4vw,2.2rem)] font-bold leading-tight tracking-[-0.02em] text-neutral-950">
                Y todo lo que viene atrás de un turno.
              </h3>
              <div className="mt-12">
                <MosaicoFuncionalidades />
              </div>
            </div>
          </div>
        </section>

        {/* PARA QUIÉN ES — la banda de color, con los rubros adentro.

            EL FONDO ES UN `background` DE LA PROPIA SECCIÓN, no una capa
            absoluta encima. Es a propósito y viene de haberlo hecho al revés:
            la versión anterior ponía un `<div absolute inset-0>` con el shader,
            y no se veía nunca. Un div de color plano en el mismo lugar tampoco
            se veía, así que el problema no era el shader — era la capa. Pintar
            la sección directamente saca del medio todo el apilado.

            Alto por contenido, sin `min-h` ni `overflow-hidden`. Aquella
            sección reservaba una pantalla entera y quedaba un hueco blanco.

            El degradado imita lo que hace el shader —color en los costados,
            centro limpio— y ese centro limpio es donde va el panel blanco. Sin
            grano todavía: eso lo agrega el shader en el paso siguiente. */}
        <section
          id="rubros"
          className="relative z-10 px-5 py-24 sm:px-8 sm:py-28 lg:px-10 2xl:px-14"
          style={{
            background: [
              'radial-gradient(58% 78% at 0% 50%, hsl(14 100% 57% / 0.55), hsl(45 100% 51% / 0.38) 45%, transparent 74%)',
              'radial-gradient(58% 78% at 100% 50%, hsl(340 82% 52% / 0.50), hsl(14 100% 57% / 0.36) 45%, transparent 74%)',
              'hsl(0 0% 100%)',
            ].join(', '),
          }}
        >
          {/* EL GRANO VA ENCIMA DEL DEGRADADO, NO EN LUGAR DE ÉL.

              El color ya está garantizado por el `background` de la sección; el
              shader sólo suma la textura y el movimiento. Si falla —sin WebGL,
              o por lo que sea— se degrada a un degradado lindo en vez de a
              blanco, que es lo que pasaba cuando el shader era el único fondo.

              `respaldo` va vacío a propósito: el componente pinta su propio
              degradado de reserva debajo del canvas, y acá sería un tercer
              degradado encima del de la sección. */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            <FondoDeGranos posicion={pantallaAncha ? FONDO_ANCHO : FONDO_ANGOSTO} />
          </div>

          {/* Sin borde ni sombra: lo que separa el panel del fondo es el
              contraste de color. Una sombra sobre un degradé de marca se ve
              sucia, y un borde gris al lado del naranja se ve como un recorte
              mal pegado. */}
          <div className="relative mx-auto w-full max-w-6xl rounded-[2rem] bg-white px-6 py-14 sm:px-10 sm:py-16 lg:px-14">
            {/* Sin tope de ancho: el panel ya lo pone. Con `max-w-3xl` el
                titular se partía en dos justo por la mitad de la frase. */}
            <h2 className="text-balance font-titular text-[clamp(1.9rem,3vw,2.8rem)] font-bold leading-tight tracking-[-0.02em] text-neutral-950">
              Si tu día se organiza en turnos, es para vos.
            </h2>

            {/* Ícono y nombre, sin descripción y sin caja: la pregunta que se
                contesta acá es "¿estoy yo?", y para eso el nombre alcanza.

                TRES COLUMNAS Y NO CUATRO aunque entren: los rubros son seis, y
                en cuatro columnas la segunda fila queda con dos huecos. Si
                mañana entra un séptimo, esto hay que volver a mirarlo. */}
            <ul className="mt-14 grid list-none grid-cols-1 gap-x-8 gap-y-8 p-0 sm:grid-cols-2 lg:grid-cols-3">
              {RUBROS_DE_LA_LANDING.map(({ id, name, icon }) => (
                <li key={id} className="flex items-center gap-4">
                  <span className="shrink-0 text-[2rem] leading-none" aria-hidden="true">
                    {icon}
                  </span>
                  <span className="min-w-0 text-lg font-semibold leading-snug text-neutral-950">
                    {name}
                  </span>
                </li>
              ))}
            </ul>

            {/* Sin esto, la lista lee como "si no estás acá, no es para vos", y
                no es cierto: el onboarding tiene "Otro" justamente para eso. */}
            <p className="mt-12 text-base text-neutral-500">
              ¿No es ninguno de estos? Si das turnos, funciona igual.
            </p>
          </div>
        </section>


        {/* PLANES — el mismo contenido que la landing 1, con la voz de ésta.

            Los precios y los planes salen de data/planes.js, o sea que son los
            mismos que ve el onboarding y /admin/plans y no hay una segunda
            lista que mantener. La tarjeta también es la misma
            (TarjetaDePlan), para que la landing y el producto no se vean como
            dos cosas distintas.

            Se listan `planesDeContratacion` y NO `planes`: los segundos son
            cuatro —los tres pagos con los nombres viejos más la tarjeta de la
            prueba—, así que en una grilla de tres columnas la cuarta cae sola
            abajo, y encima anuncia un plan con un nombre distinto del que
            aparece al pagar. La prueba de 14 días se ofrece en el hero.

            Lo que NO se copió de Home es la envoltura: el titular usa la
            tipografía y la escala fluida de esta página, y no hay `Aparecer`.
            Son dos landings con dos voces; lo que se comparte es el contenido.

            `bg-white` por lo mismo que lo llevan las otras secciones: el fondo
            de granos abarca toda la página y sin papel blanco detrás la mancha
            del shader se transparenta sobre las tarjetas. */}
        <section id="precios" className="relative z-10 bg-white px-5 py-24 sm:px-8 sm:py-28 lg:px-10 2xl:px-14">
          <div className="mx-auto w-full max-w-7xl">
            <header className="mb-14 max-w-2xl">
              <h2 className="text-balance font-titular text-[clamp(1.6rem,2.4vw,2.2rem)] font-bold leading-tight tracking-[-0.02em] text-neutral-950">
                Planes
              </h2>
              <p className="mt-3 max-w-xl text-pretty text-base leading-relaxed text-neutral-600 sm:text-lg">
                {/* "por tamaño de equipo" porque es lo que separa un plan de
                    otro (cuánta gente y cuántos lugares entran), y "sin llamar
                    a nadie" porque tener que pedir la baja por teléfono es una
                    objeción real contra la que se compara este producto. */}
                Elegí por tamaño de equipo. Cambiás o cancelás cuando quieras,
                sin llamar a nadie.
              </p>
            </header>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {planesDeContratacion.map((plan) => (
                <TarjetaDePlan key={plan.id} plan={plan} className="w-full">
                  {/* El plan cotizado no se registra solo: en el backend es
                      `cobrable: false`, así que mandarlo a /auth/register lo
                      dejaría en un alta que no puede terminar en nada. Se saca
                      un turno con nosotros, en nuestra propia página pública
                      (`plan.enlace`), que además es la demo del producto. */}
                  <Button
                    asChild
                    variant={!plan.cotizado && plan.destacado ? 'default' : 'outline'}
                    className="w-full"
                  >
                    <Link to={plan.cotizado ? plan.enlace : '/auth/register'}>
                      {plan.cta}
                    </Link>
                  </Button>
                </TarjetaDePlan>
              ))}
            </div>
          </div>
        </section>

        {/* PREGUNTAS FRECUENTES — el mismo bloque que la landing 1.

            No se copió nada: `Faq2` lee las preguntas y las categorías de
            data/faqs.js por su cuenta, así que montarlo acá es una línea y una
            pregunta nueva entra en las dos landings a la vez.

            Ya venía haciendo falta por otro motivo: esta página emite
            `faqSchema(faqs)` en su SEO desde antes de tener el bloque, o sea
            que le declaraba a Google preguntas y respuestas que no estaban en
            la página. Eso es lo que se cierra acá.

            El <section> con `bg-white` es por lo mismo que lo lleva la sección
            de producto: el fondo de granos abarca toda la página y el propio
            Faq2 viene con `bg-white/90`, así que sin papel blanco detrás la
            mancha del shader se le transparenta encima. */}
        <section id="recursos" className="relative z-10 bg-white px-5 pb-24 sm:px-8 sm:pb-32 lg:px-10 2xl:px-14">
          <div className="mx-auto w-full max-w-7xl">
            <Faq2 />
          </div>
        </section>
        </main>

        {/* El mismo cierre que la landing 1: `<Footer {...footerData} mostrarEquipos />`,
            idéntico carácter por carácter. `mostrarEquipos` agrega la maqueta del
            producto arriba de las columnas; en /demo y /admin/plans va sin ella.

            VA ADENTRO DEL ENVOLTORIO, no después. En Home el footer está dentro
            del mismo contenedor que el resto de la página; acá había quedado
            afuera, como hermano del envoltorio, y en esa posición las columnas
            ocupaban sus 500px sin llegar a pintarse. */}
        <Footer {...footerData} mostrarEquipos />
      </div>
    </div>
  );
};

export default LandingDos;
